import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import {
  Bell,
  Pin,
  Calendar,
  CheckCircle,
  Plus,
  Users,
  User,
  Trash2,
} from 'lucide-react';
import { formatDistanceToNow, format, isAfter } from 'date-fns';
import { es } from 'date-fns/locale';
import type { StaffAlert, StaffAlertType } from '@/types/legacy';
import { StaffAlertForm } from './StaffAlertForm';

const alertTypeConfig: Record<StaffAlertType, { label: string; icon: string; color: string }> = {
  schedule: { label: 'Horario', icon: '🕐', color: 'bg-blue-500/10 text-blue-600' },
  vacation: { label: 'Vacaciones', icon: '🏖️', color: 'bg-green-500/10 text-green-600' },
  salary: { label: 'Nómina', icon: '💰', color: 'bg-yellow-500/10 text-yellow-600' },
  reduction: { label: 'Reducción', icon: '⏰', color: 'bg-orange-500/10 text-orange-600' },
  calendar: { label: 'Calendario', icon: '📅', color: 'bg-purple-500/10 text-purple-600' },
  meeting: { label: 'Reunión', icon: '👥', color: 'bg-indigo-500/10 text-indigo-600' },
  training: { label: 'Formación', icon: '📚', color: 'bg-cyan-500/10 text-cyan-600' },
  policy: { label: 'Normativa', icon: '📋', color: 'bg-gray-500/10 text-gray-600' },
  urgent: { label: 'Urgente', icon: '🚨', color: 'bg-red-500/10 text-red-600' },
  info: { label: 'Info', icon: 'ℹ️', color: 'bg-sky-500/10 text-sky-600' },
  other: { label: 'Otro', icon: '📌', color: 'bg-slate-500/10 text-slate-600' },
};

interface StaffAlertsListProps {
  viewMode: 'employee' | 'admin';
}

export function StaffAlertsList({ viewMode }: StaffAlertsListProps) {
  const { user, userRole } = useAuth();
  const { toast } = useToast();
  const [alerts, setAlerts] = useState<StaffAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [confirmedAlerts, setConfirmedAlerts] = useState<Set<string>>(new Set());

  const fetchAlerts = async () => {
    if (!user) return;

    try {
      let query = api.from('staff_alerts').select('*');

      if (viewMode === 'employee') {
        // Empleados ven solo alertas activas para ellos
        query = query
          .eq('is_active', true)
          .or(`target_employee_id.is.null,target_employee_id.eq.${user.id}`)
          .or(`target_role.is.null,target_role.eq.all,target_role.eq.${userRole}`);
      }

      const { data, error } = await query.order('is_pinned', { ascending: false })
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Filtrar alertas expiradas
      const now = new Date();
      const validAlerts = (data || []).filter(alert => {
        if (!alert.expiry_date) return true;
        return isAfter(new Date(alert.expiry_date), now);
      });

      setAlerts(validAlerts);

      // Cargar confirmaciones del usuario
      if (viewMode === 'employee') {
        const { data: confirmations } = await api
          .from('alert_confirmations')
          .select('alert_id')
          .eq('employee_id', user.id);

        if (confirmations) {
          setConfirmedAlerts(new Set(confirmations.map(c => c.alert_id)));
        }
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las comunicaciones',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlerts();
  }, [user, viewMode]);

  const handleConfirm = async (alertId: string) => {
    if (!user) return;

    try {
      const { error } = await api
        .from('alert_confirmations')
        .insert({
          alert_id: alertId,
          employee_id: user.id,
          employee_name: user.email,
        });

      if (error) throw error;

      setConfirmedAlerts(prev => new Set([...prev, alertId]));
      toast({
        title: 'Confirmado',
        description: 'Has confirmado la lectura de esta comunicación',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'No se pudo confirmar la lectura',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (alertId: string) => {
    if (!confirm('¿Estás seguro de eliminar esta comunicación?')) return;

    try {
      const { error } = await api
        .from('staff_alerts')
        .update({ is_active: false })
        .eq('id', alertId);

      if (error) throw error;

      toast({
        title: 'Eliminada',
        description: 'La comunicación ha sido eliminada',
      });
      fetchAlerts();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'No se pudo eliminar la comunicación',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const canCreate = viewMode === 'admin' && (userRole === 'admin' || userRole === 'supervisor');
  const pendingConfirmations = alerts.filter(
    a => a.requires_confirmation && !confirmedAlerts.has(a.id)
  ).length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Bell className="w-5 h-5" />
            {viewMode === 'admin' ? 'Gestión de Comunicaciones' : 'Comunicaciones de Personal'}
          </h3>
          <p className="text-sm text-muted-foreground">
            {viewMode === 'admin'
              ? 'Alertas enviadas a los empleados'
              : 'Información importante de la empresa'}
          </p>
        </div>
        {canCreate && (
          <Button onClick={() => setFormOpen(true)} className="bg-gradient-magma">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Comunicación
          </Button>
        )}
      </div>

      {/* Stats para empleados */}
      {viewMode === 'employee' && pendingConfirmations > 0 && (
        <Card className="p-4 bg-yellow-500/10 border-yellow-500/30">
          <div className="flex items-center gap-3">
            <Bell className="w-5 h-5 text-yellow-600" />
            <span className="font-medium">
              Tienes {pendingConfirmations} comunicación(es) pendientes de confirmar
            </span>
          </div>
        </Card>
      )}

      {/* Lista de alertas */}
      {alerts.length === 0 ? (
        <Card className="p-8 text-center">
          <Bell className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No hay comunicaciones</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {alerts.map((alert) => {
            const config = alertTypeConfig[alert.alert_type];
            const isConfirmed = confirmedAlerts.has(alert.id);
            const needsConfirmation = alert.requires_confirmation && !isConfirmed;

            return (
              <Card
                key={alert.id}
                className={`p-4 transition-all ${
                  alert.is_pinned ? 'border-primary/50 bg-primary/5' : ''
                } ${needsConfirmation ? 'ring-2 ring-yellow-500/50' : ''}`}
              >
                <div className="space-y-3">
                  {/* Header */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">{config.icon}</span>
                      <div>
                        <h4 className="font-semibold flex items-center gap-2">
                          {alert.is_pinned && <Pin className="w-4 h-4 text-primary" />}
                          {alert.title}
                        </h4>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={config.color} variant="secondary">
                            {config.label}
                          </Badge>
                          {alert.priority >= 7 && (
                            <Badge variant="destructive">Prioridad Alta</Badge>
                          )}
                          {alert.target_role === 'all' ? (
                            <Badge variant="outline" className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              Todos
                            </Badge>
                          ) : alert.target_employee_id ? (
                            <Badge variant="outline" className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              Individual
                            </Badge>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    {viewMode === 'admin' && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(alert.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>

                  {/* Mensaje */}
                  <p className="text-sm whitespace-pre-wrap">{alert.message}</p>

                  {/* Fechas */}
                  {(alert.effective_date || alert.expiry_date) && (
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      {alert.effective_date && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          Desde: {format(new Date(alert.effective_date), 'dd/MM/yyyy')}
                        </span>
                      )}
                      {alert.expiry_date && (
                        <span className="flex items-center gap-1">
                          Hasta: {format(new Date(alert.expiry_date), 'dd/MM/yyyy')}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-2 border-t">
                    <span className="text-xs text-muted-foreground">
                      {alert.created_by_name && `Por ${alert.created_by_name} · `}
                      {formatDistanceToNow(new Date(alert.created_at), {
                        addSuffix: true,
                        locale: es
                      })}
                    </span>

                    {viewMode === 'employee' && alert.requires_confirmation && (
                      isConfirmed ? (
                        <Badge variant="outline" className="text-green-600">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Confirmado
                        </Badge>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleConfirm(alert.id)}
                        >
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Confirmar lectura
                        </Button>
                      )
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {canCreate && (
        <StaffAlertForm
          open={formOpen}
          onOpenChange={setFormOpen}
          onSuccess={fetchAlerts}
        />
      )}
    </div>
  );
}
